import { useState, useEffect, useCallback, useRef } from 'react';
import { Conversion, ConversionStatus } from '../types';
import { conversionsApi } from '../api/conversions';

interface UseConversionsOptions {
  limit?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseConversionsReturn {
  conversions: Conversion[];
  loading: boolean;
  error: string | null;
  total: number;
  currentPage: number;
  pageSize: number;
  fetchConversions: (page?: number) => Promise<void>;
  createConversion: (url: string, title?: string) => Promise<Conversion>;
  createTextConversion: (content: string, title?: string) => Promise<Conversion>;
  deleteConversion: (id: string) => Promise<void>;
  refreshConversion: (id: string) => Promise<void>;
}

const PROCESSING_STATUSES: ConversionStatus[] = ['queued', 'processing'];

export default function useConversions(
  options: UseConversionsOptions = {}
): UseConversionsReturn {
  const { limit = 10, autoRefresh = true, refreshInterval = 5000 } = options;

  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const conversionsRef = useRef<Conversion[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    conversionsRef.current = conversions;
  }, [conversions]);

  const fetchConversions = useCallback(
    async (page: number = 1) => {
      setLoading(true);
      setError(null);

      try {
        const offset = (page - 1) * limit;
        const response = await conversionsApi.getConversions(limit, offset);

        setConversions(response.items);
        setTotal(response.total);
        setCurrentPage(page);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to fetch conversions';
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [limit]
  );

  const createConversion = useCallback(
    async (url: string, title?: string): Promise<Conversion> => {
      const newConversion = await conversionsApi.createConversion(url, title);

      // Add to the beginning of the list
      setConversions((prev) => [newConversion, ...prev.slice(0, limit - 1)]);
      setTotal((prev) => prev + 1);

      return newConversion;
    },
    [limit]
  );

  const createTextConversion = useCallback(
    async (content: string, title?: string): Promise<Conversion> => {
      const newConversion = await conversionsApi.createTextConversion(
        content,
        title
      );

      setConversions((prev) => [newConversion, ...prev.slice(0, limit - 1)]);
      setTotal((prev) => prev + 1);

      return newConversion;
    },
    [limit]
  );

  const deleteConversion = useCallback(async (id: string): Promise<void> => {
    await conversionsApi.deleteConversion(id);

    setConversions((prev) => prev.filter((c) => c.id !== id));
    setTotal((prev) => prev - 1);
  }, []);

  const refreshConversion = useCallback(async (id: string): Promise<void> => {
    try {
      const updated = await conversionsApi.getConversion(id);
      setConversions((prev) =>
        prev.map((c) => (c.id === id ? updated : c))
      );
    } catch (err) {
      console.error('Failed to refresh conversion:', err);
    }
  }, []);

  // Poll for status updates on processing conversions
  useEffect(() => {
    if (!autoRefresh) return;

    const pollProcessingConversions = async () => {
      // Use ref to get current conversions without stale closure
      const processingConversions = conversionsRef.current.filter((c) =>
        PROCESSING_STATUSES.includes(c.status)
      );

      if (processingConversions.length === 0) return;

      const processingIds = processingConversions.map((c) => c.id);
      const statusPromises = processingIds.map((id) =>
        conversionsApi.getConversionStatus(id).catch(() => null)
      );

      const statuses = await Promise.all(statusPromises);

      // Update status for all conversions
      setConversions((prev) =>
        prev.map((conversion) => {
          const statusIndex = processingIds.indexOf(conversion.id);
          if (statusIndex === -1) return conversion;

          const status = statuses[statusIndex];
          if (!status) return conversion;

          return {
            ...conversion,
            status: status.status,
            progress: status.progress,
            errorMessage: status.errorMessage,
          };
        })
      );

      // Check if any completed and we need full data
      // Map status results back to their conversion IDs
      const completedIds = statuses
        .map((s, index) => (s?.status === 'completed' ? processingIds[index] : null))
        .filter(Boolean) as string[];

      // Fetch full data for completed conversions
      if (completedIds.length > 0) {
        await Promise.all(completedIds.map(id => refreshConversion(id)));
      }
    };

    pollingRef.current = setInterval(pollProcessingConversions, refreshInterval);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, refreshConversion]);

  // Initial fetch
  useEffect(() => {
    fetchConversions(1);
  }, [fetchConversions]);

  return {
    conversions,
    loading,
    error,
    total,
    currentPage,
    pageSize: limit,
    fetchConversions,
    createConversion,
    createTextConversion,
    deleteConversion,
    refreshConversion,
  };
}
